/**
 * @file OrderPickingSection.tsx
 * @description Блок «Сборка» в карточке заказа (phase19.14, экран C шага 0).
 *
 * Отвечает менеджеру на два вопроса: идёт ли сборка и что с ней не так.
 * Задание грузится отдельным запросом (`GET /admin/orders/:id/picking`):
 * данные нужны только на стадии «На сборке», а карточка заказа и так тяжёлая.
 *
 * Разбор недостачи (список позиций и решения менеджера, включая возврат
 * задания в очередь) вынесен в `OrderPickingShortage` — здесь остаётся статус,
 * прогресс и ссылка на задание.
 *
 * Якорь `#picking` (реестр `ORDER_CARD_ANCHOR`) — запасная цель главной
 * кнопки: если узел «Сборка» в «Пути заказа» скрыт складскими правами и
 * пришёл без ссылки, кнопка «Решить недостачу» ведёт сюда, а не пропадает.
 */

import { AlertTriangle, ArrowRight, Info, Loader2, PackageCheck } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";

import { OrderPickingShortage } from "./OrderPickingShortage";
import { PickingStatusBadge } from "../wms/picking/PickingStatusBadge";
import { Button } from "../ui/button";
import { EditorialCard } from "../ui/EditorialCard";
import { useHasPermission } from "../../auth/hooks";
import {
  pickingApi,
  pickingProgressPercent,
  type PickingTaskDetailDto,
} from "../../lib/api-picking";
import { ORDER_CARD_ANCHOR } from "../../lib/order-chain-links";
import { formatPickedPositions } from "../../lib/picking-format";

interface Props {
  orderId: string;
  /** Slug магазина — ссылка «Открыть задание». */
  shopSlug?: string;
}

export function OrderPickingSection({ orderId, shopSlug }: Props) {
  const canManage = useHasPermission("SHOP_WMS_MANAGE");
  const [task, setTask] = useState<PickingTaskDetailDto | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setTask(await pickingApi.forOrder(orderId));
    } catch {
      // Нет прав на склад или сборка не заводилась — блок просто не рисуем.
      setTask(null);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  if (loading) {
    return (
      <EditorialCard>
        <EditorialCard.Body>
          <div className="flex items-center gap-2 text-sm text-on-surface-variant">
            <Loader2 className="size-4 animate-spin" />
            Загружаем сборку…
          </div>
        </EditorialCard.Body>
      </EditorialCard>
    );
  }
  if (!task) return null;

  const isShortage = task.status === "SHORTAGE";
  // Отметки о нехватке видны и до закрытия исхода — менеджер следит за сборкой.
  const hasShortage =
    isShortage || task.items.some((i) => Number(i.shortageQty) > 0);

  return (
    <EditorialCard id={ORDER_CARD_ANCHOR.picking}>
      <EditorialCard.Body>
        <div className="flex flex-wrap items-center gap-2">
          {isShortage ? (
            <AlertTriangle className="size-4 text-destructive" aria-hidden />
          ) : (
            <PackageCheck className="size-4 text-primary" aria-hidden />
          )}
          <span className="label-editorial">
            {isShortage ? "Сборка: недостача" : "Сборка"}
          </span>
          <span className="font-mono text-sm">{task.number}</span>
          <PickingStatusBadge status={task.status} />
        </div>

        <dl className="mt-3 grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
          <Field label="Склад" value={task.warehouse.name} />
          <Field
            label="Исполнитель"
            value={
              task.assignee
                ? `${task.assignee.displayName ?? "сотрудник склада"}${
                    task.startedAt
                      ? ` · с ${new Date(task.startedAt).toLocaleString("ru-RU")}`
                      : ""
                  }`
                : "не взято в работу"
            }
          />
        </dl>

        <div className="mt-3 flex items-center gap-3">
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-surface-low">
            <div
              className="h-full rounded-full bg-primary"
              style={{ width: `${pickingProgressPercent(task)}%` }}
            />
          </div>
          <span className="shrink-0 text-sm tabular-nums text-on-surface-variant">
            {formatPickedPositions(task.pickedCount, task.itemsCount)}
          </span>
        </div>

        {hasShortage ? (
          <OrderPickingShortage
            task={task}
            canManage={canManage}
            onReleased={() => void load()}
          />
        ) : null}

        {shopSlug ? (
          <div className="mt-3">
            <Button variant="outline" size="sm" asChild className="min-h-11">
              <Link to={`/shop/${shopSlug}/wms/picking/${task.id}`}>
                Открыть задание
                <ArrowRight className="ml-1.5 size-4" />
              </Link>
            </Button>
          </div>
        ) : null}

        <p className="mt-3 flex items-start gap-1.5 text-xs text-on-surface-variant">
          <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          <span>
            {isShortage
              ? "Заказ остаётся «На сборке» до решения. Задание — в «Требуют внимания»."
              : "Перевести заказ в «Собран» вручную нельзя — стадия меняется по факту проведения сборки."}
          </span>
        </p>
      </EditorialCard.Body>
    </EditorialCard>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-on-surface-variant">
        {label}
      </dt>
      <dd className="mt-0.5 font-medium">{value}</dd>
    </div>
  );
}
